import { useState, useEffect } from "react";
import { api, useAuth } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Trash2,
  Loader2,
  Copy,
  CheckCircle,
  Key,
  Upload,
  ImageIcon,
  X,
  Building2,
  Sparkles,
  Pencil,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getRoleLabel, getRoleColor } from "@/lib/roles";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const generateSecurePassword = (length = 12) => {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lowercase = 'abcdefghjkmnpqrstuvwxyz';
  const numbers = '23456789';
  const symbols = '@#$%&*!?';
  const allChars = uppercase + lowercase + numbers + symbols;
  let password = '';
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });

  // Forms
  const [userForm, setUserForm] = useState({ email: "", first_name: "", role: "chef_chantier" });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState({ email: "", password: "" });
  const [copied, setCopied] = useState(false);

  // Logo
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupStats, setCleanupStats] = useState(null);

  // Edit user
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [editForm, setEditForm] = useState({ email: "", first_name: "", new_password: "", role: "", admin_password: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);

  const handleCleanup = async () => {
    setCleaningUp(true);
    setCleanupStats(null);
    try {
      const res = await api.post("/admin/cleanup-taches");
      setCleanupStats(res.data.stats);
      const s = res.data.stats;
      const total = s.descriptions_nettoyees + s.dates_corrigees + s.doublons_supprimes + s.lignes_vides_supprimees;
      if (total > 0) {
        toast.success(`Nettoyage terminé : ${total} correction${total > 1 ? "s" : ""} appliquée${total > 1 ? "s" : ""}`);
      } else {
        toast.info("Aucune donnée parasite détectée");
      }
    } catch (e) {
      toast.error("Erreur lors du nettoyage");
    } finally {
      setCleaningUp(false);
    }
  };

  const openEditDialog = (u) => {
    setEditForm({
      email: u.email,
      first_name: u.first_name || "",
      new_password: "",
      role: u.role || "chef_chantier",
      admin_password: "",
    });
    setShowEditPassword(false);
    setShowAdminPassword(false);
    setEditDialog({ open: true, user: u });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editForm.admin_password) {
      toast.error("Veuillez saisir votre mot de passe admin pour confirmer");
      return;
    }
    setEditSaving(true);
    try {
      const payload = { admin_password: editForm.admin_password };
      if (editForm.email !== editDialog.user.email) payload.email = editForm.email;
      if (editForm.first_name !== (editDialog.user.first_name || "")) payload.first_name = editForm.first_name;
      if (editForm.new_password) payload.new_password = editForm.new_password;
      if (editForm.role !== editDialog.user.role) payload.role = editForm.role;

      const res = await api.put(`/users/${editDialog.user.id}`, payload);
      toast.success(res.data.message || "Utilisateur mis à jour");
      setEditDialog({ open: false, user: null });
      loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Erreur lors de la modification");
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const requests = [api.get("/users")];
      if (user?.company_id) {
        requests.push(api.get(`/companies/${user.company_id}`));
      }
      const results = await Promise.all(requests);
      setUsers(results[0].data);
      if (results[1]) setCompany(results[1].data);
    } catch (error) {
      console.error("Error loading admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Logo handlers
  const handleLogoUpload = async (file) => {
    if (!file || !company) return;
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/companies/${company.id}/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo mis a jour");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!company) return;
    try {
      await api.delete(`/companies/${company.id}/logo`);
      toast.success("Logo supprime");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // User handlers
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    const generatedPassword = generateSecurePassword(12);
    try {
      await api.post("/users", {
        ...userForm,
        company_id: user.company_id,
        password: generatedPassword
      });
      setCreatedCredentials({ email: userForm.email, password: generatedPassword });
      setShowUserModal(false);
      setShowCredentialsModal(true);
      setCopied(false);
      setUserForm({ email: "", first_name: "", role: "chef_chantier" });
      setPrivacyAccepted(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCredentials = async () => {
    const text = `Email: ${createdCredentials.email}\nMot de passe: ${createdCredentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Identifiants copies");
    setTimeout(() => setCopied(false), 3000);
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/users/${deleteDialog.id}`);
      toast.success("Utilisateur supprime");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  if (loading) {
    return (
      <MobileLayout title="Administration">
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title="Administration">
      <div className="px-4 lg:px-6 py-6 space-y-6 animate-fade-in">
        {/* Company card */}
        {company && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="company-info-card">
            <div className="flex items-center gap-4">
              {/* Logo */}
              <div className="relative flex-shrink-0 group">
                {company.logo_url ? (
                  <div className="relative">
                    <img
                      src={`${BACKEND_URL}${company.logo_url}`}
                      alt="Logo"
                      className="w-14 h-14 rounded-xl object-cover border border-zinc-700"
                    />
                    <button
                      onClick={handleDeleteLogo}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="delete-logo-btn"
                    >
                      <X size={12} className="text-white" />
                    </button>
                  </div>
                ) : (
                  <label className="w-14 h-14 bg-zinc-800 rounded-xl flex items-center justify-center cursor-pointer hover:bg-zinc-700 transition-colors border border-zinc-700 border-dashed">
                    {uploadingLogo ? (
                      <Loader2 size={20} className="text-zinc-500 animate-spin" />
                    ) : (
                      <ImageIcon size={20} className="text-zinc-500" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e.target.files[0])}
                      data-testid="upload-logo-input"
                    />
                  </label>
                )}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-lg truncate">{company.name}</h3>
                <p className="text-zinc-500 text-sm truncate">{company.email}</p>
              </div>
              {/* Upload button when logo exists */}
              {company.logo_url && (
                <label className="flex-shrink-0">
                  <div className="btn-secondary h-9 px-3 flex items-center gap-1.5 cursor-pointer text-sm rounded-lg border border-zinc-700 text-zinc-400 hover:text-zinc-300 transition-colors">
                    {uploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    <span className="hidden lg:inline">Changer</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleLogoUpload(e.target.files[0])}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Header with user count + add button */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg flex items-center gap-2">
              <Users size={20} className="text-orange-500" /> Utilisateurs
            </h2>
            <p className="text-zinc-500 text-sm mt-0.5">{users.length} membre{users.length !== 1 ? "s" : ""}</p>
          </div>
          <Button
            onClick={() => setShowUserModal(true)}
            className="btn-primary h-10 px-4"
            data-testid="add-user-btn"
          >
            <Plus size={18} className="mr-1" /> Ajouter
          </Button>
        </div>

        {/* Data cleanup section */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="cleanup-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <Sparkles size={16} className="text-orange-500" />
                Nettoyage des données
              </h3>
              <p className="text-zinc-500 text-xs mt-1">
                Supprime les artefacts (———), dates aberrantes, doublons et lignes vides des tâches CR
              </p>
            </div>
            <Button
              onClick={handleCleanup}
              disabled={cleaningUp}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:text-orange-400 hover:border-orange-500/30 gap-1.5 flex-shrink-0"
              data-testid="cleanup-btn"
            >
              {cleaningUp ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Lancer le nettoyage
            </Button>
          </div>
          {cleanupStats && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cleanup-stats">
              <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                <div className="text-white font-bold text-lg">{cleanupStats.descriptions_nettoyees}</div>
                <div className="text-zinc-500 text-[10px] uppercase">Descriptions</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                <div className="text-white font-bold text-lg">{cleanupStats.dates_corrigees}</div>
                <div className="text-zinc-500 text-[10px] uppercase">Dates</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                <div className="text-white font-bold text-lg">{cleanupStats.doublons_supprimes}</div>
                <div className="text-zinc-500 text-[10px] uppercase">Doublons</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                <div className="text-white font-bold text-lg">{cleanupStats.lignes_vides_supprimees}</div>
                <div className="text-zinc-500 text-[10px] uppercase">Lignes vides</div>
              </div>
            </div>
          )}
        </div>

        {/* User list */}
        {users.length === 0 ? (
          <div className="empty-state py-8">
            <Users size={40} className="mx-auto mb-3 text-zinc-600" />
            <p className="text-zinc-400 mb-4">Aucun utilisateur</p>
            <Button onClick={() => setShowUserModal(true)} className="btn-primary" data-testid="create-first-user">
              <Plus size={18} className="mr-2" /> Creer un utilisateur
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="user-list">
            {users.map((u) => (
              <div
                key={u.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3"
                data-testid={`user-item-${u.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  <span className="text-zinc-400 text-sm font-bold">
                    {(u.first_name || u.email)[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-white font-medium truncate">{u.first_name || u.email.split("@")[0]}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${getRoleColor(u.role)}`}>
                      {getRoleLabel(u.role)}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-xs truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEditDialog(u)}
                    className="text-zinc-600 hover:text-orange-400 transition-colors p-2"
                    data-testid={`edit-user-${u.id}`}
                    title="Modifier"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => {
                      if (u.id === user?.id) {
                        toast.error("Impossible de supprimer votre propre compte");
                        return;
                      }
                      setDeleteDialog({ open: true, id: u.id, name: u.first_name || u.email });
                    }}
                    className="text-zinc-600 hover:text-red-400 transition-colors p-2"
                    data-testid={`delete-user-${u.id}`}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Nouvel utilisateur</DialogTitle>
            <p className="text-zinc-400 text-sm">Un mot de passe sera genere automatiquement</p>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Prenom / Nom</Label>
              <Input
                value={userForm.first_name}
                onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                placeholder="Jean Martin"
                required
                className="input-dark"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="jean@entreprise.fr"
                required
                className="input-dark"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Role</Label>
              <select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                className="select-dark w-full"
                data-testid="user-role-select"
              >
                <option value="chef_chantier">Chef de chantier</option>
                <option value="conducteur_travaux">Conducteur de travaux</option>
              </select>
            </div>
            <div className="flex items-start gap-2 pt-1">
              <input
                type="checkbox"
                id="privacy-consent"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                required
                className="mt-0.5 accent-orange-500 cursor-pointer"
                data-testid="privacy-consent-checkbox"
              />
              <label htmlFor="privacy-consent" className="text-zinc-400 text-xs leading-snug cursor-pointer">
                {"J'accepte la "}
                <Link to="/privacy" target="_blank" className="text-orange-400 hover:text-orange-300 underline" data-testid="privacy-consent-link">
                  politique de confidentialité
                </Link>
              </label>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => { setShowUserModal(false); setPrivacyAccepted(false); }} className="btn-secondary flex-1">Annuler</Button>
              <Button type="submit" disabled={saving || !privacyAccepted} className="btn-primary flex-1" data-testid="user-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={20} /> : "Créer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle size={24} className="text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Utilisateur cree</h3>
                <p className="text-zinc-400 text-sm">Identifiants de connexion</p>
              </div>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 space-y-3">
              <div className="space-y-1">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Email (identifiant)</p>
                <p className="text-white font-mono text-sm break-all" data-testid="cred-email">{createdCredentials.email}</p>
              </div>
              <div className="border-t border-zinc-800 pt-3 space-y-1">
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Mot de passe genere</p>
                <div className="flex items-center gap-2">
                  <Key size={16} className="text-orange-500" />
                  <p className="text-orange-400 font-mono text-sm" data-testid="cred-password">{createdCredentials.password}</p>
                </div>
              </div>
            </div>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
              <p className="text-orange-300 text-sm text-center">Sauvegardez ces identifiants. Le mot de passe ne sera plus affiche.</p>
            </div>
            <Button
              onClick={handleCopyCredentials}
              className={`w-full h-12 font-semibold transition-all ${copied ? "bg-green-600 hover:bg-green-600 text-white" : "btn-primary"}`}
              data-testid="copy-credentials-btn"
            >
              {copied ? <><CheckCircle size={18} className="mr-2" /> Identifiants copies</> : <><Copy size={18} className="mr-2" /> Copier les identifiants</>}
            </Button>
            <Button onClick={() => setShowCredentialsModal(false)} className="btn-secondary w-full">Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, user: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Modifier l{"'"}utilisateur</DialogTitle>
            <p className="text-zinc-400 text-sm">
              {editDialog.user?.first_name || editDialog.user?.email}
            </p>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Nom</Label>
              <Input
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                placeholder="Prénom Nom"
                className="input-dark"
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="email@entreprise.fr"
                required
                className="input-dark"
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Rôle</Label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="w-full select-dark h-10 px-3 rounded-md text-sm"
                data-testid="edit-user-role"
              >
                <option value="conducteur_travaux">Conducteur de travaux</option>
                <option value="chef_chantier">Chef de chantier</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                Nouveau mot de passe <span className="text-zinc-600 normal-case">(laisser vide pour ne pas changer)</span>
              </Label>
              <div className="relative">
                <Input
                  type={showEditPassword ? "text" : "password"}
                  value={editForm.new_password}
                  onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                  placeholder="Nouveau mot de passe..."
                  className="input-dark pr-10"
                  data-testid="edit-user-password"
                />
                <button
                  type="button"
                  onClick={() => setShowEditPassword(!showEditPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showEditPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="border-t border-zinc-800 pt-4 space-y-2">
              <Label className="text-orange-400 text-sm uppercase tracking-wider flex items-center gap-1.5">
                <Key size={14} /> Confirmation admin
              </Label>
              <p className="text-zinc-500 text-xs">Saisissez votre mot de passe pour valider la modification</p>
              <div className="relative">
                <Input
                  type={showAdminPassword ? "text" : "password"}
                  value={editForm.admin_password}
                  onChange={(e) => setEditForm({ ...editForm, admin_password: e.target.value })}
                  placeholder="Votre mot de passe admin..."
                  required
                  className="input-dark pr-10"
                  data-testid="edit-admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showAdminPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setEditDialog({ open: false, user: null })} className="btn-secondary flex-1">Annuler</Button>
              <Button type="submit" disabled={editSaving || !editForm.admin_password} className="btn-primary flex-1" data-testid="edit-user-submit">
                {editSaving ? <Loader2 className="animate-spin" size={20} /> : "Enregistrer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: null, name: "" })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Supprimer l{"'"}utilisateur <span className="text-white font-medium">{deleteDialog.name}</span> ? Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 text-white">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MobileLayout>
  );
};

export default AdminPage;
